import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, Leaf, Droplets, Wrench, Sparkles, MapPin, ShieldCheck, Handshake, ScrollText } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { GallerySection } from "@/components/GallerySection";
import { ReviewsSection } from "@/components/ReviewsSection";
import { CONTACT } from "@/data/site";

const ease = [0.22, 1, 0.36, 1];
const IMG = {
  hero: "https://images.unsplash.com/photo-1758448756207-54505680d130?auto=format&fit=crop&w=1400&q=80",
  garden: "https://images.pexels.com/photos/34174881/pexels-photo-34174881.jpeg?auto=compress&cs=tinysrgb&w=1200",
  wash: "/photos/facade-echafaudage.webp",
  path: "https://images.unsplash.com/photo-1764670325965-618c104b0b07?auto=format&fit=crop&w=1200&q=80",
  flowers: "https://images.unsplash.com/photo-1597201278257-3687be27d954?auto=format&fit=crop&w=1200&q=80",
};

const HeroLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const services = [
  { icon: Leaf, title: "Parcs & Jardins", text: "Entretien, création, restauration et remise en état de vos espaces verts.", to: "/parcs-jardins", cta: "En savoir plus" },
  { icon: Droplets, title: "Nettoyage extérieur", text: "Façades, allées, cours, terrasses et autres surfaces extérieures.", to: "/nettoyage-exterieur", cta: "En savoir plus" },
  { icon: Wrench, title: "Petites réparations", text: "Petits travaux, réparations et interventions diverses.", to: "/petites-reparations", cta: "En savoir plus" },
  { icon: Sparkles, title: "Entreprise polyvalente", text: "Une seule entreprise pour entretenir, nettoyer et améliorer vos extérieurs.", to: "/a-propos", cta: "Découvrir l'entreprise" },
];

const why = [
  { icon: Sparkles, title: "Travail soigné", text: "Une attention particulière portée à la qualité des finitions." },
  { icon: Handshake, title: "Polyvalence", text: "Entretien, nettoyage, restauration et petits travaux." },
  { icon: MapPin, title: "Proximité", text: "Une entreprise implantée dans les Alpes-Maritimes." },
  { icon: ScrollText, title: "Devis", text: "Possibilité de demander un devis pour votre projet." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div data-testid="home-page">
      <Seo
        title="Sandro Services 06 — Entretien de jardins et nettoyage extérieur dans les Alpes-Maritimes"
        description="Sandro Services 06 : entretien de jardins, débroussaillage, création d'espaces verts, nettoyage de façades et petites réparations dans les Alpes-Maritimes (06). Demandez votre devis."
      />

      {/* HERO */}
      <section ref={heroRef} className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-center pt-6 md:pt-10">
          <div>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 rounded-full border border-line bg-white/60 px-4 py-2 text-xs font-medium text-forest mb-8"
            >
              <MapPin size={14} className="text-olive" /> Intervention dans les Alpes-Maritimes (06)
            </motion.div>

            <h1 className="font-display text-[15vw] sm:text-7xl lg:text-[5.6rem] leading-[0.92] text-forest tracking-tight">
              <HeroLine delay={0.15}>Sandro</HeroLine>
              <HeroLine delay={0.28}>Services <span className="text-olive">06</span></HeroLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8, ease }}
              className="mt-8 text-xl md:text-2xl font-display italic text-ink max-w-xl"
            >
              L'entretien et la rénovation de vos extérieurs, avec soin et professionnalisme.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.72, duration: 0.8, ease }}
              className="mt-5 text-base text-ink-soft max-w-lg leading-relaxed"
            >
              Sandro Services 06 vous accompagne pour l'entretien, la création et la restauration
              de vos espaces extérieurs, ainsi que pour vos travaux de nettoyage et petites réparations.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8, ease }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <Link to="/services" data-testid="hero-services-btn" className="inline-flex items-center justify-center gap-2 bg-olive text-white rounded-full px-8 py-4 font-semibold hover:bg-olive-hover transition-all duration-300 hover:-translate-y-1 shadow-md">
                Découvrir nos services <ArrowRight size={18} />
              </Link>
              <Link to="/contact" data-testid="hero-devis-btn" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-forest text-forest px-8 py-4 font-semibold hover:bg-forest hover:text-sand transition-all duration-300">
                Demander un devis
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3, duration: 1, ease }}
            className="relative h-[380px] sm:h-[480px] lg:h-[620px] overflow-hidden arch-top rounded-b-2xl grain shadow-lift"
          >
            <motion.img
              src={IMG.hero} alt="Jardin paysager entretenu"
              style={{ y, scale }}
              className="absolute inset-0 w-full h-[120%] object-cover"
            />
            <div className="absolute bottom-5 left-5 right-5 backdrop-blur-md bg-forest/70 text-sand rounded-2xl px-5 py-4 flex items-center justify-between">
              <span className="text-sm font-medium">Espaces verts · Nettoyage · Réparations</span>
              <ArrowUpRight size={20} className="text-olive" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="mt-20 md:mt-28 border-y border-line bg-white/50 py-6">
        <Marquee speed={40} gradient={false} className="overflow-hidden">
          {["Tonte", "Débroussaillage", "Taille", "Création paysagère", "Nettoyage de façades", "Traitement anti-mousse", "Hydrofuge", "Petites réparations", "Restauration de jardins"].map((w, i) => (
            <span key={i} className="mx-8 font-display text-3xl md:text-4xl text-forest/80 italic flex items-center gap-8">
              {w} <span className="text-olive not-italic">✳</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* SERVICES */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.4em] text-olive font-semibold">01 — Nos services</span>
          <h2 className="mt-5 font-display text-4xl md:text-5xl text-forest tracking-tight">
            Une entreprise, tous vos extérieurs
          </h2>
        </Reveal>

        <Stagger className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {services.map((s) => (
            <StaggerItem key={s.title}>
              <Link to={s.to} data-testid={`service-card-${s.to.slice(1)}`} className="group flex flex-col h-full bg-white border border-line rounded-2xl p-8 hover:shadow-lift transition-all duration-300 hover:-translate-y-1">
                <div className="w-12 h-12 rounded-full bg-sand-100 grid place-items-center text-olive group-hover:bg-olive group-hover:text-white transition-colors duration-300">
                  <s.icon size={22} />
                </div>
                <h3 className="mt-6 font-display text-2xl text-forest">{s.title}</h3>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed flex-1">{s.text}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-olive group-hover:gap-3 transition-all">
                  {s.cta} <ArrowRight size={16} />
                </span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* PARCS & JARDINS */}
      <section className="bg-white border-y border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative h-[380px] lg:h-[520px] rounded-2xl overflow-hidden grain shadow-soft order-2 lg:order-1">
            <img src={IMG.garden} alt="Entretien de jardin" className="w-full h-full object-cover" />
          </Reveal>
          <Reveal delay={0.1} className="order-1 lg:order-2">
            <span className="text-xs uppercase tracking-[0.4em] text-olive font-semibold">02 — Parcs & Jardins</span>
            <h2 className="mt-5 font-display text-4xl md:text-5xl text-forest tracking-tight leading-[1.05]">
              Entretien, création et restauration de parcs & jardins
            </h2>
            <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-ink">
              {["Tonte", "Débroussaillage", "Taille", "Désherbage", "Nettoyage", "Remise en état", "Création de massifs", "Aménagement paysager", "Entretien régulier", "Évacuation des déchets verts"].map((t) => (
                <span key={t} className="flex items-center gap-2"><Leaf size={14} className="text-olive shrink-0" /> {t}</span>
              ))}
            </div>
            <Link to="/parcs-jardins" data-testid="home-parcs-btn" className="mt-10 inline-flex items-center gap-2 text-forest font-semibold border-b-2 border-olive pb-1 hover:gap-3 transition-all">
              Voir tous les services Parcs & Jardins <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* NETTOYAGE EXTÉRIEUR */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.4em] text-olive font-semibold">03 — Nettoyage extérieur</span>
          <h2 className="mt-5 font-display text-4xl md:text-5xl text-forest tracking-tight leading-[1.05]">
            Redonnez de l'éclat à vos extérieurs
          </h2>
          <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-ink">
            {["Nettoyage de façades", "Nettoyage d'allées", "Nettoyage de cours", "Nettoyage de terrasses", "Nettoyage haute pression", "Traitement anti-mousse", "Traitement hydrofuge", "Remise en état des surfaces"].map((t) => (
              <span key={t} className="flex items-center gap-2"><Droplets size={14} className="text-olive shrink-0" /> {t}</span>
            ))}
          </div>
          <Link to="/nettoyage-exterieur" data-testid="home-nettoyage-btn" className="mt-10 inline-flex items-center gap-2 text-forest font-semibold border-b-2 border-olive pb-1 hover:gap-3 transition-all">
            Découvrir le nettoyage extérieur <ArrowRight size={16} />
          </Link>
        </Reveal>
        <Reveal delay={0.1} className="relative h-[380px] lg:h-[520px] rounded-2xl overflow-hidden grain shadow-soft">
          <img src={IMG.wash} alt="Nettoyage de façade par Sandro Services 06 dans les Alpes-Maritimes" className="w-full h-full object-cover" />
        </Reveal>
      </section>

      {/* PETITES RÉPARATIONS */}
      <section className="bg-forest text-sand grain">
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.4em] text-olive font-semibold">04 — Petites réparations</span>
            <h2 className="mt-5 font-display text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Des petites réparations pour entretenir votre extérieur
            </h2>
            <p className="mt-6 text-lg text-sand/80 leading-relaxed">
              Sandro Services 06 réalise des interventions de petite ampleur pour prendre soin de vos
              espaces extérieurs. Petits travaux et réparations courantes selon la nature de l'intervention.
            </p>
            <Link to="/petites-reparations" data-testid="home-reparations-btn" className="mt-10 inline-flex items-center gap-2 bg-olive text-white rounded-full px-8 py-4 font-semibold hover:bg-olive-hover transition-all hover:-translate-y-1 shadow-md">
              Découvrir les petites réparations <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* POURQUOI CHOISIR */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.4em] text-olive font-semibold">Pourquoi nous</span>
          <h2 className="mt-5 font-display text-4xl md:text-5xl text-forest tracking-tight">
            Pourquoi choisir Sandro Services 06 ?
          </h2>
        </Reveal>
        <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {why.map((w) => (
            <StaggerItem key={w.title} className="bg-white border border-line rounded-2xl p-8 hover:shadow-soft transition-all">
              <w.icon size={26} className="text-olive" />
              <h3 className="mt-5 font-display text-2xl text-forest">{w.title}</h3>
              <p className="mt-3 text-sm text-ink-soft leading-relaxed">{w.text}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* RÉALISATIONS (avant/après) */}
      <GallerySection />

      {/* AVIS CLIENTS */}
      <ReviewsSection />

      {/* FINAL CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <Reveal className="relative overflow-hidden rounded-[2rem] bg-olive text-white px-8 md:px-16 py-16 md:py-24 grain">
          <div className="relative max-w-2xl">
            <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.02]">
              Un projet d'extérieur ? Parlons-en.
            </h2>
            <p className="mt-6 text-white/85 text-lg">
              Décrivez votre besoin et recevez un devis adapté à votre projet dans les Alpes-Maritimes.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" data-testid="cta-devis-btn" className="inline-flex items-center justify-center gap-2 bg-white text-forest rounded-full px-8 py-4 font-semibold hover:-translate-y-1 transition-all">
                Demander un devis <ArrowRight size={18} />
              </Link>
              <a href={`tel:${CONTACT.phone}`} data-testid="cta-call-btn" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/70 px-8 py-4 font-semibold hover:bg-white/10 transition-all">
                Appeler le {CONTACT.phoneDisplay}
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
